import React, { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, ClipboardCheck } from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { WorkflowCreationDialog } from '@/components/ApprovalStageWizard/WorkflowCreationDialog';
import { WorkflowDetailsPage } from '@/components/ApprovalStageWizard/WorkflowDetailsPage';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { usePermissions } from '@/contexts/PermissionContext';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import {
  useApprovalDialogState,
  useStageDraft,
  useWorkflowActions,
  useWorkflowData,
  useWorkflowDerivedState,
} from '@/features/approvals/components/approval-workflows/hooks';
import {
  QuickStartTemplates,
  WorkflowGrid,
} from '@/features/approvals/components/approval-workflows/components';
import {
  WorkflowAssignDialogWrapper,
  WorkflowDeleteDialog,
  WorkflowDuplicateDialogWrapper,
} from '@/features/approvals/components/approval-workflows/dialogs';

export const ApprovalTestPage: React.FC = () => {
  const { isLive } = useScreenDataMode('settings-approvals');
  const { canManageApprovalWorkflows } = usePermissions();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreationDialog, setShowCreationDialog] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<'list' | 'visual'>('list');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  const {
    workflows,
    setWorkflows,
    employees,
    isLoading,
    error,
    loadWorkflows,
    loadEmployees,
  } = useWorkflowData(isLive);

  const { approverUsers, workflowAssignmentCounts, approvals, filteredApprovals } =
    useWorkflowDerivedState(workflows, employees, isLive, searchQuery, statusFilter);

  const dialogs = useApprovalDialogState({ workflows });

  const { newStageIdToEdit, newStageName, handleAddStage, handleClearInitialEditingStageId } =
    useStageDraft({ isLive, selectedWorkflowId, workflows, setWorkflows });

  const handleWorkflowCreated = useCallback((workflowId: string) => {
    setShowCreationDialog(false);
    setSelectedWorkflowId(workflowId);
    setSelectedTemplate(null);
  }, []);

  const handleWorkflowDeleted = useCallback((workflowId: string) => {
    // If viewing the deleted workflow, go back to list
    setSelectedWorkflowId((current) => (current === workflowId ? null : current));
  }, []);

  const {
    handleCreateWorkflow,
    handleUpdateStage,
    handleSaveDraft,
    handleDeleteStage,
    handleReorderStages,
    handleDuplicateStageInWorkflow,
    handleUpdateWorkflowStatus,
    handleDeleteWorkflow,
    handleDuplicate,
    handleAssign,
    isDeleting,
  } = useWorkflowActions({
    isLive,
    selectedWorkflowId,
    workflows,
    setWorkflows,
    employees,
    loadWorkflows,
    loadEmployees,
    onWorkflowCreated: handleWorkflowCreated,
    onWorkflowDeleted: handleWorkflowDeleted,
    onDeleteSettled: dialogs.closeDeleteDialog,
    onWorkflowDuplicated: dialogs.closeDuplicateDialog,
  });

  const handleTemplateClick = (templateName: string) => {
    setSelectedTemplate(templateName);
    setShowCreationDialog(true);
  };

  const handleNewWorkflow = () => {
    setSelectedTemplate(null);
    setShowCreationDialog(true);
  };

  const handleBackToList = () => {
    setSelectedWorkflowId(null);
    setCurrentView('list');
  };

  const selectedWorkflow = workflows.find((w) => w.id === selectedWorkflowId);
  // Hydrate selectedWorkflow with computed assignedToCount from employees data
  const selectedWorkflowWithCount = selectedWorkflow ? {
    ...selectedWorkflow,
    assignedToCount: workflowAssignmentCounts[selectedWorkflow.id] ?? 0,
  } : undefined;

  // Access denied guard
  if (!canManageApprovalWorkflows()) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Timesheet Approval Workflows"

          size={36}
        />
        <AccessRestricted message="You don't have permission to manage approval workflows. Contact your administrator to request access." />
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading workflows...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <p className="text-destructive">{error}</p>
          <Button onClick={loadWorkflows}>Try Again</Button>
        </div>
      </div>
    );
  }

  // If viewing a workflow, show the details page
  if (selectedWorkflowWithCount) {
    return (
      <WorkflowDetailsPage
        workflow={selectedWorkflowWithCount}
        onBack={handleBackToList}
        onUpdateStage={handleUpdateStage}
        onSaveDraft={isLive ? handleSaveDraft : undefined}
        onDeleteStage={handleDeleteStage}
        onAddStage={handleAddStage}
        onUpdateWorkflowStatus={handleUpdateWorkflowStatus}
        onReorderStages={handleReorderStages}
        onDuplicateStage={handleDuplicateStageInWorkflow}
        onDeleteWorkflow={dialogs.openDeleteDialog}
        currentView={currentView}
        onViewChange={setCurrentView}
        initialEditingStageId={newStageIdToEdit}
        onClearInitialEditingStageId={handleClearInitialEditingStageId}
        initialStageName={newStageName}
        approverUsers={approverUsers}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-start gap-4 min-w-0">
        <div className="flex-1 min-w-0">
          <PageHeader
            title="Timesheet Approval Workflows"

            size={36}
            showBreadcrumb={false}
            icon={<ClipboardCheck />}
            actions={
              <Button className="flex items-center gap-2" onClick={handleNewWorkflow}>
                <Plus className="h-4 w-4" />
                New Workflow
              </Button>
            }
          />
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <FeatureFeedbackCollector feature="Approval Workflows" />
        </div>
      </div>

      {/* Quick Start Templates - Conditional Display */}
      <QuickStartTemplates
        hasWorkflows={approvals.length > 0}
        onTemplateClick={handleTemplateClick}
      />

      <WorkflowGrid
        filteredApprovals={filteredApprovals}
        allApprovals={approvals}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onViewWorkflow={setSelectedWorkflowId}
        onAssignWorkflow={dialogs.handleAssignWorkflow}
        onDuplicateWorkflow={dialogs.handleDuplicateWorkflow}
        onDeleteWorkflow={dialogs.openDeleteDialog}
      />

      {/* Workflow Creation Dialog */}
      <WorkflowCreationDialog
        isOpen={showCreationDialog}
        onClose={() => setShowCreationDialog(false)}
        onCreated={(data) => handleCreateWorkflow(data, selectedTemplate)}
      />

      {/* Workflow Assignment Dialog */}
      <WorkflowAssignDialogWrapper
        open={dialogs.assignDialogOpen}
        onOpenChange={dialogs.handleAssignOpenChange}
        approval={approvals.find((a) => a.id === dialogs.workflowToAssign) ?? null}
        employees={employees}
        onAssign={handleAssign}
      />

      {/* Workflow Duplication Dialog */}
      <WorkflowDuplicateDialogWrapper
        open={dialogs.duplicateDialogOpen}
        onOpenChange={dialogs.handleDuplicateOpenChange}
        workflow={dialogs.workflowToDuplicate}
        onDuplicate={handleDuplicate}
      />

      {/* Workflow Delete Confirmation Dialog */}
      <WorkflowDeleteDialog
        open={dialogs.deleteDialogOpen}
        onOpenChange={dialogs.handleDeleteOpenChange}
        workflowName={workflows.find((w) => w.id === dialogs.workflowToDelete)?.name}
        assignedEmployeeCount={
          dialogs.workflowToDelete ? workflowAssignmentCounts[dialogs.workflowToDelete] || 0 : 0
        }
        isDeleting={isDeleting}
        onConfirm={() => dialogs.workflowToDelete && handleDeleteWorkflow(dialogs.workflowToDelete)}
      />
    </div>
  );
};
